// frontend/src/utils/idempotentRetry.js
// 過渡的な通信障害に対する「冪等GET限定」リトライの分類ロジック（純関数）。
// Mobile 参照実装（mobile/src/services/apiInternals.ts）と定数・挙動を揃える:
//   RETRYABLE_GET_STATUSES = {502, 504}
//   GET_RETRY_DELAYS_MS = [500, 1500]
//   getRetryDelayMs のジッター = base * (0.7 + Math.random()*0.6)
//
// 安全第一: 変更系（POST/PUT/PATCH/DELETE 等）は絶対にリトライ対象にしない。
// GET/HEAD/OPTIONS かつ 502/504 かネットワークエラー のみを対象にする。

// リトライしてよい安全（冪等）メソッド。
export const SAFE_RETRY_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

// 過渡障害としてリトライしてよい HTTP ステータス（Mobile と一致）。
// 500 / 503 / 423 / 4xx(429含む) は含めない。
export const RETRYABLE_GET_STATUSES = new Set([502, 504]);

// リトライ間隔（ms）。長さがそのまま最大リトライ回数になる（＝2回）。
export const GET_RETRY_DELAYS_MS = [500, 1500];

export const MAX_GET_RETRY_ATTEMPTS = GET_RETRY_DELAYS_MS.length;

export function isSafeRetryMethod(method) {
  return SAFE_RETRY_METHODS.has(String(method || 'GET').toUpperCase());
}

// 呼び出し側キャンセル（AbortController）はリトライしない。
export function isAbortError(error) {
  if (!error) return false;
  return error.name === 'AbortError' || error.code === 20 || error.code === 'ABORT_ERR';
}

// リトライ対象の HTTP ステータス（502/504 のみ）。
export function isRetryableStatus(status) {
  return RETRYABLE_GET_STATUSES.has(Number(status));
}

// fetch 自体の reject（ネットワーク断）を過渡障害とみなす。TypeError が代表。
// AbortError（キャンセル）は除外する。
export function isRetryableNetworkError(error) {
  if (!error) return false;
  if (isAbortError(error)) return false;
  if (error instanceof TypeError) return true;
  return error.name === 'TypeError';
}

// 応答（Response が返った）に対するリトライ判定。
export function shouldRetryGetResponse({ method, status } = {}) {
  return isSafeRetryMethod(method) && isRetryableStatus(status);
}

// 例外（fetch が reject）に対するリトライ判定。
export function shouldRetryGetError({ method, error } = {}) {
  return isSafeRetryMethod(method) && isRetryableNetworkError(error);
}

// attempt 回目（0 始まり）のバックオフ待ち時間。ジッターは Mobile と同一。
export function getRetryDelayMs(attempt, random = Math.random) {
  const base = GET_RETRY_DELAYS_MS[attempt] || 0;
  if (!base) return 0;
  return Math.round(base * (0.7 + random() * 0.6));
}
